/**
 * Workflow Registry — maps agent_type to layout configuration.
 *
 * Each entry defines:
 *  - label:            Human-readable name for the workflow
 *  - toolSet:          Backend tool set identifier sent with LLM requests
 *  - panels:           Which widget panel components to render in the right zone
 *  - fileUploader:     Legacy onboarding modal config (null = no file upload step)
 *  - enableMentions:   Whether @-mention search is enabled in the chat input
 *
 * The AiAssistant reads this registry to dynamically compose the widget zone
 * when the user switches between threads of different agent types.
 */
import {
    FILE_ACCEPT_FORMATS,
    BID_QUICKSTART_FILE_ACCEPT_FORMATS,
} from "@/ai/constants";
import {
    ChatRoundDots,
    DocumentAdd,
    BillList,
    DocumentText,
    FolderWithFiles,
    SortByTime,
} from "@solar-icons/react";

export const WORKFLOW_REGISTRY = {
    router: {
        label: "New Chat",
        icon: ChatRoundDots,
        toolSet: "project_router",
        panels: [],
        fileUploader: null,
        enableMentions: false,
        layoutDefaults: {
            sidebarSplit: 0.15,
        },
    },
    intake: {
        label: "Request Project",
        icon: DocumentAdd,
        toolSet: "quickstart",
        panels: ["canvas"],
        fileUploader: null,
        enableMentions: false,
        title: "Request Project",
        description:
            "Describe what you need and attach any reference files you'd like to provide.",
        suggestions: [
            "Start a new project brief",
            "I have documents to upload",
        ],
        layoutDefaults: {
            sidebarSplit: 0.2,
            mainSplit: 0.5,
        },
    },
    brief: {
        label: "Project Brief",
        icon: DocumentAdd,
        toolSet: "project_brief",
        panels: ["canvas"],
        fileUploader: {
            title: "AI Quick Start",
            description:
                "Drag and drop any documents related to the project here. Notes, spreadsheets, chats, transcripts - anything goes.\nYou can copy and paste a summary or write something in a new note.",
            buttonCreateText: "Create Brief",
            buttonAddText: "Add Sources",
            acceptedFormats: FILE_ACCEPT_FORMATS,
        },
        enableMentions: false,
        layoutDefaults: {
            sidebarSplit: 0.2,
            mainSplit: 0.5,
        },
    },
    wrap_report: {
        label: "Wrap Report",
        icon: FolderWithFiles,
        toolSet: "project_wrap_report",
        panels: ["canvas"],
        fileUploader: {
            title: "Wrap Report",
            description:
                "I will create a wrap report based this project's data and bids, but feel free to upload any additional wrap-related documents — final deliverables, schedules, budget reports, or notes.",
            buttonCreateText: "Create Report",
            buttonAddText: "Add Documents",
            acceptedFormats: FILE_ACCEPT_FORMATS,
        },
        enableMentions: false,
        layoutDefaults: {
            sidebarSplit: 0.2,
            mainSplit: 0.5,
        },
    },
    timeline: {
        label: "Project Timeline",
        icon: SortByTime,
        toolSet: "project_timeline",
        panels: ["canvas"],
        fileUploader: null,
        enableMentions: false,
        layoutDefaults: {
            sidebarSplit: 0.2,
            mainSplit: 0.5,
        },
    },
    bid: {
        label: "Bid Import",
        icon: BillList,
        toolSet: "bid_quickstart",
        panels: ["bidPreview", "wizard"],
        fileUploader: {
            title: "Smart Bid Import",
            description:
                "Upload your client's Bid Sheet, PDF specification, or project description here.",
            buttonCreateText: "Start Bid Import",
            buttonAddText: "Add Documents",
            acceptedFormats: BID_QUICKSTART_FILE_ACCEPT_FORMATS,
            maxFiles: 1,
        },
        enableMentions: false,
        layoutDefaults: {
            sidebarSplit: 0.25,
            mainSplit: 0.5,
            wizardSplit: 0.95,
        },
    },
    bid_create: {
        label: "Bid Create",
        icon: DocumentText,
        toolSet: "bid_create",
        panels: [],
        fileUploader: {
            title: "Create a Bid",
            description:
                "Upload any reference documents — project specs, scope documents, or past bids — to help build your new bid.",
            buttonCreateText: "Start Bid Creation",
            buttonAddText: "Add Documents",
            acceptedFormats: FILE_ACCEPT_FORMATS,
        },
        enableMentions: false,
        layoutDefaults: {
            sidebarSplit: 0.2,
        },
    },
};

/**
 * Returns the workflow config for a given agent type, falling back to router.
 */
export function getWorkflowConfig(agentType) {
    return WORKFLOW_REGISTRY[agentType] || WORKFLOW_REGISTRY.router;
}
